import React, { useCallback, useState } from 'react';

import { OptionKey } from '@/common/types';
import { ALL_SERVICE_TYPES_KEY } from '@/modules/ProcessingDefinition/Services/Services.constants';
import {
  CategoryItem,
  ServiceItem,
  ServiceTypeValues,
} from '@/modules/ProcessingDefinition/Services/Services.types';
import { useCategoryExpand } from '@/modules/ProcessingDefinition/Services/widgets/CategoryCard/hooks';
import useGetServicesByCategoryQuery from '@/modules/ProcessingDefinition/Services/widgets/CategoryCard/queries/useGetServicesByCategory.query';

import CategoryRow from './components/CategoryRow';
import { CHECK_STATE, CheckState } from './hooks';

interface CategoryRowContainerProps {
  category: CategoryItem;
  checkState: CheckState;
  isServiceSelected: (serviceId: string) => boolean;
  onToggleCategory: (category: CategoryItem, allServices?: ServiceItem[]) => void;
  onToggleService: (service: ServiceItem) => void;
  onStartLoading: () => void;
  onEndLoading: () => void;
}

const CategoryRowContainer: React.FC<CategoryRowContainerProps> = ({
  category,
  checkState,
  isServiceSelected,
  onToggleCategory,
  onToggleService,
  onStartLoading,
  onEndLoading,
}) => {
  const [isLoadingAll, setIsLoadingAll] = useState(false);

  const {
    isExpanded,
    searchString,
    currentSearch,
    serviceTypeFilter,
    page,
    perPage,
    handleToggleExpand,
    handleSearchChange,
    handleServiceTypeChange,
    handlePageChange,
    handlePerPageChange,
  } = useCategoryExpand();

  const {
    services,
    isLoading: isServicesLoading,
    total: servicesTotal,
  } = useGetServicesByCategoryQuery(
    {
      categoryType: category.name,
      name: currentSearch || undefined,
      serviceType: serviceTypeFilter,
      page: page - 1,
      size: perPage,
    },
    isExpanded
  );

  const { refetch: refetchAllServices } = useGetServicesByCategoryQuery(
    {
      categoryType: category.name,
      size: category.serviceCount,
      page: 0,
    },
    false
  );

  const handleToggleCategory = useCallback(async () => {
    if (checkState === CHECK_STATE.CHECKED) {
      onToggleCategory(category);
      return;
    }

    // If expanded and all services fit on one page — use already loaded services
    if (isExpanded && servicesTotal <= perPage) {
      onToggleCategory(category, services);
      return;
    }

    // Fetch all services for selection (don't expand — user can expand manually)
    setIsLoadingAll(true);
    onStartLoading();
    try {
      const result = await refetchAllServices();
      const allServices =
        result.data?.data?.content?.map((item) => ({
          id: String(item.id),
          name: item.name || '',
          serviceType: item.service_type || ServiceTypeValues.MANUAL_CLAIM,
          categoryType: category.name,
        })) || [];
      onToggleCategory(category, allServices);
    } finally {
      setIsLoadingAll(false);
      onEndLoading();
    }
  }, [
    checkState,
    isExpanded,
    servicesTotal,
    perPage,
    onStartLoading,
    onToggleCategory,
    category,
    services,
    refetchAllServices,
    onEndLoading,
  ]);

  const handleToggleService = useCallback(
    (service: ServiceItem) => {
      onToggleService(service);
    },
    [onToggleService]
  );

  const handleServiceTypeFilterChange = useCallback(
    (value: OptionKey) => {
      handleServiceTypeChange((value as string) || ALL_SERVICE_TYPES_KEY);
    },
    [handleServiceTypeChange]
  );

  return (
    <CategoryRow
      category={category}
      isExpanded={isExpanded}
      checkState={checkState}
      isLoadingAll={isLoadingAll}
      services={services}
      servicesTotal={servicesTotal}
      isServicesLoading={isServicesLoading}
      searchString={searchString}
      serviceTypeFilter={serviceTypeFilter}
      page={page}
      perPage={perPage}
      isServiceSelected={isServiceSelected}
      onToggleExpand={handleToggleExpand}
      onToggleCategory={handleToggleCategory}
      onToggleService={handleToggleService}
      onSearchChange={handleSearchChange}
      onServiceTypeChange={handleServiceTypeFilterChange}
      onPageChange={handlePageChange}
      onPerPageChange={handlePerPageChange}
    />
  );
};

export default CategoryRowContainer;
